/**
 * useChartRenderer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * React hook that owns the entire D3 rendering lifecycle for MarketPulze charts.
 *
 * Responsibilities:
 *   - SVG initialisation (with ResizeObserver + RAF height fix)
 *   - doRenderChart()   — full canvas repaint (axes, candles, overlays, signals)
 *   - doRenderPanels()  — RSI/MACD/other sub-panel repaints
 *   - updateLegendValues() — crosshair indicator tooltip values
 *   - D3 zoom handler
 *   - ResizeObserver (Fix 1: reads actual px after layout, never CSS string)
 *   - All drawing interaction handlers (click, mousemove, rightclick)
 *   - Keyboard handlers (arrow nav, ?, L, Escape)
 *   - Price alert click zone (y-axis margin)
 *   - Returns all refs that useChartData needs as inputs
 *
 * Architecture rules (must be maintained):
 *   1. doRenderChart and doRenderPanels are useCallback(fn, []) — stable identity.
 *      Every variable they access comes through a ref. Never through a closure
 *      over state or props — that creates stale reads after React batching.
 *   2. SVG init useEffect has [] deps and runs exactly once on mount.
 *      buildSVG() is called from inside the RAF — never before layout.
 *   3. No fetch calls, no WebSocket — those live in useChartData.js.
 *   4. No JSX — this hook returns refs, callbacks, and primitive state only.
 *   5. All D3 signal logging uses logRef.current?.() — never logRef directly.
 *
 * Ref ownership model:
 *   useChartRenderer CREATES all refs.
 *   PriceChart.jsx RECEIVES them via destructuring the hook return.
 *   PriceChart.jsx PASSES data refs to useChartData as input params.
 *   useChartData WRITES to the data refs (candlesRef, chartDataRef, etc.).
 *   useChartRenderer READS those same refs at paint time via doRenderChart.
 *
 *   Bridge refs (created here, written by useChartData, read here):
 *     earnEventsRef, signalBadgesRef, candlePatternsRef
 *   These are returned and passed by PriceChart.jsx to useChartData.
 *
 * Fix 1 (chart height bug — live in screenshots):
 *   SVG height is read via getBoundingClientRect() after requestAnimationFrame.
 *   The container div uses flex:1 (fills remaining viewport height).
 *   clientHeight was unreliable before CSS layout paint — getBCR is not.
 *   500px fallback is gone. If height is 0 after RAF, we retry.
 *
 * Fix 2 (pattern clutter):
 *   Pattern badges are filtered by activePatterns before painting.
 *   Default: activePatterns = [] → nothing rendered until user opts in.
 *
 * Fix 3 (drawer overlay):
 *   Chart height is driven by flex:1 on the container div in PriceChart.jsx.
 *   This hook reads the actual rendered height via ResizeObserver.
 *   The drawer's open/closed state has zero effect on innerH.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';

import {
  CHART_MARGIN, INDICATOR_REGISTRY, IND_COLORS,
  DRAWING_COLORS,
} from '../constants/chartConfig';
import {
  renderCandleLayer, renderVolumeLayer, renderOverlayLine,
  renderVolumeProfile, renderDrawings, renderMeasure,
  renderAnalystLayer, renderAlertsLayer,
} from '../lib/chartRenderers';
import { createDateIndexScale, toET, etFmt } from '../lib/chartScales';
import { fmtPrice }                           from '../utils/chartUtils';

// ── Hook ──────────────────────────────────────────────────────────────────────

export default function useChartRenderer({
  // Identity/display state (for ref syncing and rendering decisions)
  ticker,
  activeTab,
  chartType,
  activeIndicators,
  activePatterns,   // [{key, instanceId}] — patterns to render (Fix 2)
  comboSignals,     // [{index, ...}] — combination signals to render
  isLoggedIn,
  drawings,
  analystVisible,
  analystData,
  priceAlerts,
  mode,             // "fullscreen" | "embedded" | "tab" | "floating"

  // Setters — D3 handlers call back into React state via these
  setDrawings,
  setDrawingMode,
  setDrawingHint,
  setAlertPopover,

  // Analytics
  logRef,
  saveDrawingRef,   // stable ref to saveDrawingToBackend fn (set by PriceChart)
}) {

  // ── Refs owned by this hook ────────────────────────────────────────────────

  // DOM
  const containerRef      = useRef(null);
  const gRef              = useRef(null);        // main SVG <g> node

  // Scales
  const xScaleRef         = useRef(null);
  const xScaleBaseRef     = useRef(null);
  const yScaleRef         = useRef(null);
  const zoomRef           = useRef(null);

  // Dimensions
  const innerWRef         = useRef(0);
  const innerHRef         = useRef(0);

  // Data (written by useChartData, read by doRenderChart)
  const candlesRef        = useRef([]);
  const chartDataRef      = useRef([]);
  const tradingDatesRef   = useRef([]);
  const dateToIndexRef    = useRef(new Map());

  // Bridge refs — created here, passed to useChartData, written there, read here
  const earnEventsRef     = useRef([]);
  const signalBadgesRef   = useRef([]);
  const candlePatternsRef = useRef([]);

  // WebSocket refs (created here, passed to useChartData)
  const wsRef             = useRef(null);
  const wsReconnectRef    = useRef(null);
  const wsLiveRef         = useRef(false);
  const wsTickerRef       = useRef(null);

  // Stable ref mirrors for D3 closures (avoids stale state reads)
  const activeTabRef      = useRef(activeTab);
  const chartTypeRef      = useRef(chartType);
  const activeIndRef      = useRef(activeIndicators);
  const activePatternsRef = useRef(activePatterns);
  const comboSignalsRef   = useRef(comboSignals);
  const isLoggedInRef     = useRef(isLoggedIn);
  const showExtHoursRef   = useRef(false);
  const analystVisibleRef = useRef(analystVisible);
  const priceAlertsRef    = useRef(priceAlerts);
  const analystDataRef    = useRef(analystData);

  // Drawing state refs
  const drawingModeRef    = useRef(null);
  const isDrawingRef      = useRef(false);
  const trendStartRef     = useRef(null);
  const fibStartRef       = useRef(null);
  const rectStartRef      = useRef(null);
  const measureStartRef   = useRef(null);
  const measureRef        = useRef(null);
  const drawingDwellRef   = useRef(null);
  const drawingIdRef      = useRef(0);
  const drawingsRef       = useRef(drawings);

  // Panel containers keyed by instanceId
  const panelContainersRef = useRef({});

  // Crosshair
  const ohlcvRef            = useRef(null);
  const crosshairLockedRef  = useRef(false);
  const hoverDebounce       = useRef(null);

  // ── Keep ref mirrors in sync with prop changes ─────────────────────────────
  // These run on every render to keep D3 closures reading current values.
  activeTabRef.current      = activeTab;
  chartTypeRef.current      = chartType;
  activeIndRef.current      = activeIndicators;
  activePatternsRef.current = activePatterns;
  comboSignalsRef.current   = comboSignals;
  isLoggedInRef.current     = isLoggedIn;
  analystVisibleRef.current = analystVisible;
  priceAlertsRef.current    = priceAlerts;
  analystDataRef.current    = analystData;
  drawingsRef.current       = drawings;

  // ── Visual state (returned for JSX rendering) ──────────────────────────────
  const [ohlcv,           setOhlcv]           = useState(null);
  const [legendValues,    setLegendValues]     = useState({});
  const [tooltipPos,      setTooltipPos]       = useState({ x: 0, y: 0 });
  const [crosshairLocked, setCrosshairLocked]  = useState(false);
  const [showShortcuts,   setShowShortcuts]    = useState(false);
  const [ctxMenu,         setCtxMenu]          = useState(null);

  // ── updateLegendValues — crosshair indicator tooltip sync ─────────────────
  // Reads indicator values at the hovered candle index.
  // Must use candlesRef (raw {t,o,h,l,c,v}) NOT chartDataRef.
  const updateLegendValues = useCallback((candles, xHover) => {
    if (!candles.length || !activeIndRef.current.length) {
      setLegendValues({});
      return;
    }

    const isDay   = activeTabRef.current === 0;
    const idx     = xHover !== null ? Math.max(0, Math.min(xHover, candles.length - 1)) : candles.length - 1;
    const newVals = {};

    activeIndRef.current.forEach(ind => {
      const entry = INDICATOR_REGISTRY[ind.key];
      if (!entry) return;
      const color  = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
      const result = entry.calc(candles, ind.params, isDay, color);

      // Overlay lines — find value at hovered index by timestamp match
      if (result.lines) {
        result.lines.forEach((line, li) => {
          if (!line.data?.length) return;
          const targetT = candles[idx]?.t;
          const pt      = line.data.find(d => d.time === targetT)
                       || line.data[line.data.length - 1];
          if (!pt || pt.value == null) return;
          const lbl = entry.label + (li === 0 && ind.params?.period ? ` (${ind.params.period})` : '');
          newVals[`${ind.instanceId}-${li}`] = {
            label: lbl,
            value: pt.value.toFixed(2),
            color: line.color || color,
          };
        });
      }

      // PSAR — point-based indicator
      if (result.points?.length) {
        const targetT = candles[idx]?.t;
        const pt      = result.points.find(d => d.time === targetT)
                     || result.points[result.points.length - 1];
        if (pt?.value != null) {
          newVals[`${ind.instanceId}-psar`] = {
            label: entry.label,
            value: pt.value.toFixed(2),
            color: pt.color || color,
          };
        }
      }

      // MACD — special multi-value display
      if (result.macd?.macdLine?.length) {
        const targetT  = candles[idx]?.t;
        const findPt   = (arr) => arr.find(d => d.time === targetT) || arr[arr.length - 1];
        const macdPt   = findPt(result.macd.macdLine);
        const sigPt    = findPt(result.macd.signalLine);
        if (macdPt) newVals[`${ind.instanceId}-macd`]   = { label: 'MACD',   value: macdPt.value.toFixed(3), color };
        if (sigPt)  newVals[`${ind.instanceId}-signal`] = { label: 'Signal', value: sigPt.value.toFixed(3),  color: color + '88' };
      }

      // ADX — three-line display
      if (result.adx?.adx?.length) {
        const targetT = candles[idx]?.t;
        const findPt  = (arr) => arr.find(d => d.time === targetT) || arr[arr.length - 1];
        const adxPt   = findPt(result.adx.adx);
        const pdiPt   = findPt(result.adx.plusDI);
        const ndiPt   = findPt(result.adx.minusDI);
        if (adxPt) newVals[`${ind.instanceId}-adx`]  = { label: 'ADX',  value: adxPt.value.toFixed(1), color: '#00d4ff' };
        if (pdiPt) newVals[`${ind.instanceId}-pdi`]  = { label: '+DI',  value: pdiPt.value.toFixed(1), color: '#10b981' };
        if (ndiPt) newVals[`${ind.instanceId}-ndi`]  = { label: '-DI',  value: ndiPt.value.toFixed(1), color: '#ef4444' };
      }
    });

    setLegendValues(newVals);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: reads everything through refs at call time. No deps needed.

  // ── doRenderChart — main canvas repaint ───────────────────────────────────
  // useCallback(fn, []) — stable identity is critical.
  // Every variable accessed here must come through a ref.
  const doRenderChart = useCallback(() => {
    if (!gRef.current || !chartDataRef.current.length || !xScaleRef.current || !yScaleRef.current) return;

    const g      = d3.select(gRef.current);
    const xScale = xScaleRef.current;
    const yScale = yScaleRef.current;
    const innerW = innerWRef.current;
    const innerH = innerHRef.current;
    const svgEl  = gRef.current.closest('svg');
    const svg    = d3.select(svgEl);

    // S27FIX3: y-scale auto-fit — recalculate domain from only visible candles
    const [x0, x1] = xScale._linear.domain();
    const allData   = chartDataRef.current;
    const visible   = allData.filter((_, i) => i >= Math.floor(x0) - 1 && i <= Math.ceil(x1) + 1);
    const src       = visible.length >= 2 ? visible : allData;
    const vLow      = d3.min(src, d => d.low)  * 0.995;
    const vHigh     = d3.max(src, d => d.high) * 1.005;
    if (vLow != null && vHigh != null && vHigh > vLow) yScale.domain([vLow, vHigh]);

    // X-axis — drive ticks from inner linear scale, label with actual trading dates
    const tdates = tradingDatesRef.current;
    const n      = tdates.length || 1;
    const step   = Math.max(1, Math.floor(n / 6));
    const ticks  = Array.from({ length: Math.min(7, n) }, (_, k) => Math.min(k * step, n - 1));
    svg.select('.x-axis').call(
      d3.axisBottom(xScale._linear)
        .tickValues(ticks)
        .tickFormat(i => {
          const d = tdates[Math.round(i)];
          return d ? etFmt(d, activeTabRef.current) : '';
        })
    ).call(ax => {
      ax.select('.domain').attr('stroke', 'rgba(255,255,255,0.06)');
      ax.selectAll('line').attr('stroke', 'rgba(255,255,255,0.04)');
      ax.selectAll('text').attr('fill', '#8b8ba7').attr('font-size', '10px');
    });

    // Y-axis
    svg.select('.y-axis').call(d3.axisRight(yScale).ticks(6).tickFormat(d => '$' + d.toLocaleString()))
      .call(ax => {
        ax.select('.domain').attr('stroke', 'rgba(255,255,255,0.06)');
        ax.selectAll('line').attr('stroke', 'rgba(255,255,255,0.04)');
        ax.selectAll('text').attr('fill', '#8b8ba7').attr('font-size', '10px');
      });

    // Horizontal grid
    g.select('.grid-h').call(
      d3.axisLeft(yScale).ticks(6).tickSize(-innerW).tickFormat('')
    ).call(ax => {
      ax.select('.domain').remove();
      ax.selectAll('line').attr('stroke', 'rgba(255,255,255,0.04)');
      ax.selectAll('text').remove();
    });

    // Volume + candles
    renderVolumeLayer(g, chartDataRef.current, xScale, innerH);
    renderCandleLayer(g, chartDataRef.current, chartTypeRef.current, xScale, yScale);

    // Session boundary lines (1D tab)
    g.selectAll('.session-line').remove();
    if (activeTabRef.current === 0 && tradingDatesRef.current.length) {
      const data = chartDataRef.current;
      data.forEach((d, i) => {
        const et = toET(d.date);
        const h  = et.getUTCHours(), m = et.getUTCMinutes();
        if ((h === 9 && m === 30) || (h === 15 && m >= 55)) {
          const px   = xScale(d.date);
          const type = (h === 9) ? 'open' : 'close';
          if (px == null || px < 0 || px > innerW) return;
          g.append('line').attr('class', 'session-line')
            .attr('x1', px).attr('y1', 0).attr('x2', px).attr('y2', innerH)
            .attr('stroke', type === 'open' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.18)')
            .attr('stroke-width', 1).attr('stroke-dasharray', '3,3');
          g.append('text').attr('class', 'session-line')
            .attr('x', px + 3).attr('y', 10).attr('font-size', '8px').attr('font-family', 'inherit')
            .attr('fill', type === 'open' ? 'rgba(16,185,129,0.45)' : 'rgba(239,68,68,0.35)')
            .text(type === 'open' ? 'Open' : 'Close');
        }
      });
    }

    // 52-week high/low (daily+ tabs)
    g.selectAll('.week52-line').remove();
    if (activeTabRef.current >= 2 && chartDataRef.current.length > 0) {
      const data   = chartDataRef.current;
      const cutoff = new Date(Date.now() - 365 * 24 * 3600 * 1000);
      const yr     = data.filter(d => d.date >= cutoff);
      if (yr.length >= 5) {
        const hi52 = d3.max(yr, d => d.high);
        const lo52 = d3.min(yr, d => d.low);
        [[hi52, 'rgba(16,185,129,0.3)',  'rgba(16,185,129,0.45)',  '52W H'],
         [lo52, 'rgba(239,68,68,0.3)',   'rgba(239,68,68,0.45)',   '52W L']].forEach(([price, stroke, textCol, lbl]) => {
          const py = yScale(price);
          g.append('line').attr('class', 'week52-line')
            .attr('x1', 0).attr('y1', py).attr('x2', innerW).attr('y2', py)
            .attr('stroke', stroke).attr('stroke-width', 1).attr('stroke-dasharray', '4,3');
          g.append('text').attr('class', 'week52-line')
            .attr('x', 3).attr('y', py - 3).attr('font-size', '8px').attr('font-family', 'inherit')
            .attr('fill', textCol).text(`${lbl} ${fmtPrice(price)}`);
        });
      }
    }

    // Previous day close (1D tab)
    g.selectAll('.prev-close-line').remove();
    if (activeTabRef.current === 0 && chartDataRef.current.length >= 2) {
      const data    = chartDataRef.current;
      const lastET  = toET(data[data.length - 1].date);
      const todayK  = `${lastET.getUTCFullYear()}-${lastET.getUTCMonth()}-${lastET.getUTCDate()}`;
      let prevClose = null;
      for (let i = data.length - 2; i >= 0; i--) {
        const et   = toET(data[i].date);
        const dayK = `${et.getUTCFullYear()}-${et.getUTCMonth()}-${et.getUTCDate()}`;
        if (dayK === todayK) continue;
        if (et.getUTCHours() >= 9 && et.getUTCHours() < 16) { prevClose = data[i].close; break; }
      }
      if (prevClose != null) {
        const py = yScale(prevClose);
        g.append('line').attr('class', 'prev-close-line')
          .attr('x1', 0).attr('y1', py).attr('x2', innerW).attr('y2', py)
          .attr('stroke', 'rgba(148,163,184,0.35)').attr('stroke-width', 1).attr('stroke-dasharray', '5,4');
        g.append('text').attr('class', 'prev-close-line')
          .attr('x', 3).attr('y', py - 3).attr('font-size', '8px').attr('font-family', 'inherit')
          .attr('fill', 'rgba(148,163,184,0.5)').text('Prev ' + fmtPrice(prevClose));
      }
    }

    // Overlay indicators (SMA, EMA, BB, VWAP, Ichimoku, PSAR, Donchian)
    const og = g.select('.overlay-g'); og.selectAll('*').remove();
    const isDay = activeTabRef.current === 0;
    activeIndRef.current.forEach(ind => {
      const entry = INDICATOR_REGISTRY[ind.key];
      if (!entry || entry.type !== 'overlay') return;
      const color  = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
      const result = entry.calc(candlesRef.current, ind.params, isDay, color);
      if (result.lines) result.lines.forEach(l => renderOverlayLine(og, l.data, xScale, yScale, l.color, l.dash));
      if (result.cloud) {
        const { tenkan, kijun, senkouA, senkouB } = result.cloud;
        renderOverlayLine(og, tenkan,  xScale, yScale, color);
        renderOverlayLine(og, kijun,   xScale, yScale, color + '88');
        renderOverlayLine(og, senkouA, xScale, yScale, 'rgba(16,185,129,0.5)', [3, 2]);
        renderOverlayLine(og, senkouB, xScale, yScale, 'rgba(239,68,68,0.5)',  [3, 2]);
      }
      if (result.points) {
        og.selectAll(`circle.psar-${ind.instanceId}`)
          .data(result.points.filter(d => d.value != null))
          .join('circle')
          .attr('class', `psar-${ind.instanceId}`)
          .attr('cx', d => xScale(new Date(d.time * 1000)))
          .attr('cy', d => yScale(d.value))
          .attr('r', 2)
          .attr('fill', d => d.bull ? '#10b981' : '#ef4444');
      }
      if (result.volumeProfile) renderVolumeProfile(og, result.volumeProfile, yScale, innerW);
    });

    // Drawings — pass candles for aVWAP calculation
    renderDrawings(
      drawingsRef.current, xScale, yScale,
      g.select('.drawing-layer').node(),
      innerW, candlesRef.current
    );

    // fib_vp_confluence — D3 Tier 3 signal.
    // Fires when a Fibonacci level is within 0.5% of the Volume Profile POC.
    // This is a multi-confluence signal — no vendor detects or logs this combination.
    const fibDrawings = drawingsRef.current.filter(d => d.type === 'fibonacci');
    const vpInd       = activeIndRef.current.find(i => i.key === 'vprofile');
    if (fibDrawings.length && vpInd && candlesRef.current.length) {
      const entry  = INDICATOR_REGISTRY['vprofile'];
      const result = entry?.calc(candlesRef.current, vpInd.params, false, '#fff');
      const poc    = result?.volumeProfile?.pocPrice;
      if (poc) {
        fibDrawings.forEach(fib => {
          const range = fib.priceHigh - fib.priceLow;
          [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1.0].forEach(ratio => {
            const level    = fib.priceHigh - range * ratio;
            const distance = Math.abs(level - poc) / poc;
            if (distance <= 0.005) {
              logRef.current?.('fib_vp_confluence', {
                fibLevel: ratio, fibPrice: Math.round(level * 100) / 100,
                pocPrice: Math.round(poc * 100) / 100, distance: Math.round(distance * 10000) / 100,
              });
            }
          });
        });
      }
    }

    // Y-axis indicator price pins — coloured pills stacked 14px apart (anti-overlap)
    g.selectAll('.ind-ypin').remove();
    const usedYPx = [];
    activeIndRef.current.forEach(ind => {
      const entry = INDICATOR_REGISTRY[ind.key];
      if (!entry || entry.type !== 'overlay') return;
      const color  = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
      const result = entry.calc(candlesRef.current, ind.params, activeTabRef.current === 0, color);
      const pins   = [];
      (result.lines || []).forEach((line, li) => {
        if (!line.data?.length) return;
        const last = line.data[line.data.length - 1];
        if (!last || last.value == null || isNaN(last.value)) return;
        pins.push({
          value: last.value,
          color: line.color || color,
          label: li === 0 ? (entry.label + (ind.params?.period ? ` ${ind.params.period}` : '')) : '',
        });
      });
      if (result.points?.length) {
        const last = result.points[result.points.length - 1];
        if (last?.value != null && !isNaN(last.value))
          pins.push({ value: last.value, color: last.color || color, label: entry.label });
      }
      pins.forEach(pin => {
        const rawPy = yScale(pin.value);
        if (isNaN(rawPy)) return;
        const nudge = (c) => usedYPx.some(u => Math.abs(u - c) < 14) ? nudge(c + 14) : c;
        const py    = nudge(rawPy);
        usedYPx.push(py);
        const pg = g.append('g').attr('class', 'ind-ypin')
          .attr('transform', `translate(${innerW + 2},${py - 8})`);
        pg.append('rect').attr('x', 0).attr('y', 0).attr('width', 62).attr('height', 16).attr('rx', 3)
          .attr('fill', 'rgba(10,16,35,0.85)').attr('stroke', pin.color).attr('stroke-width', 1);
        pg.append('text').attr('x', 31).attr('y', 11.5).attr('text-anchor', 'middle')
          .attr('fill', pin.color).attr('font-size', '9px').attr('font-family', 'inherit')
          .text(`${pin.label ? pin.label + ' ' : ''}$${pin.value.toFixed(2)}`);
      });
    });

    // Analyst overlay (S27K)
    renderAnalystLayer(
      g.select('.analyst-layer'),
      analystVisibleRef.current ? analystDataRef.current : null,
      yScale, innerW
    );

    // Price alert lines (S27K)
    renderAlertsLayer(g.select('.alerts-layer'), priceAlertsRef.current, yScale);

    // Earnings markers
    g.selectAll('.earn-marker').remove();
    earnEventsRef.current.forEach(ev => {
      if (!ev.date) return;
      const px = xScale(ev.date);
      if (px == null || isNaN(px) || px < -10 || px > innerW + 10) return;
      const eg = g.append('g').attr('class', 'earn-marker');
      eg.append('line').attr('x1', px).attr('y1', 0).attr('x2', px).attr('y2', innerH)
        .attr('stroke', 'rgba(245,158,11,0.35)').attr('stroke-width', 1).attr('stroke-dasharray', '3,3');
      eg.append('rect').attr('x', px - 10).attr('y', 2).attr('width', 20).attr('height', 13)
        .attr('rx', 2).attr('fill', 'rgba(245,158,11,0.18)').attr('stroke', 'rgba(245,158,11,0.5)').attr('stroke-width', 1);
      eg.append('text').attr('x', px).attr('y', 12).attr('text-anchor', 'middle')
        .attr('fill', '#f59e0b').attr('font-size', '8px').attr('font-family', 'inherit').attr('font-weight', 'bold')
        .text(ev.type);
    });

    // Signal badges (RSI OB/OS, MACD flip, BB breach)
    g.selectAll('.sig-badge').remove();
    const barW = Math.min(24, Math.max(2, (xScale.range()[1] - xScale.range()[0]) / (chartDataRef.current.length || 1) * 0.65));
    signalBadgesRef.current.forEach(sig => {
      const candle = chartDataRef.current[sig.index]; if (!candle) return;
      const px     = xScale(candle.date);
      if (px == null || isNaN(px)) return;
      const isBull = sig.direction === 'bull';
      const py     = isBull ? yScale(candle.low) + 8 : yScale(candle.high) - 8;
      const sg     = g.append('g').attr('class', 'sig-badge');
      sg.append('rect').attr('x', px - barW / 2 - 1).attr('y', isBull ? py : py - 11)
        .attr('width', barW + 2).attr('height', 11).attr('rx', 2)
        .attr('fill', `${sig.color}22`).attr('stroke', sig.color).attr('stroke-width', 0.8);
      sg.append('text').attr('x', px).attr('y', isBull ? py + 8 : py - 2)
        .attr('text-anchor', 'middle').attr('fill', sig.color)
        .attr('font-size', '7px').attr('font-family', 'inherit').attr('font-weight', 'bold')
        .text(sig.label);
    });

    // Pattern badges — FIX 2: filtered by activePatterns, rendered as diamonds not text labels
    g.selectAll('.pat-badge').remove();
    const activePatternKeys = new Set(activePatternsRef.current.map(p => p.key));
    candlePatternsRef.current
      .filter(pat => activePatternKeys.has(pat.type))
      .forEach(pat => {
        const candle = chartDataRef.current[pat.index]; if (!candle) return;
        const px     = xScale(candle.date);
        if (px == null || isNaN(px)) return;
        const isBull = pat.direction === 'bull' || pat.direction === 'neutral';
        const py     = isBull ? yScale(candle.low) + 22 : yScale(candle.high) - 22;
        const pg     = g.append('g').attr('class', 'pat-badge');
        // Diamond shape (rotated square)
        pg.append('rect')
          .attr('x', px - 5).attr('y', isBull ? py - 1 : py - 9)
          .attr('width', 10).attr('height', 10)
          .attr('rx', 1)
          .attr('transform', `rotate(45, ${px}, ${isBull ? py + 4 : py - 4})`)
          .attr('fill', `${pat.color}22`).attr('stroke', pat.color).attr('stroke-width', 0.8);
        pg.append('title').text(pat.desc || pat.label); // Tooltip on hover
      });

    // Combination signal badges — larger diamond with condition count indicator
    g.selectAll('.combo-badge').remove();
    comboSignalsRef.current.forEach(sig => {
      const candle = chartDataRef.current[sig.index]; if (!candle) return;
      const px     = xScale(candle.date);
      if (px == null || isNaN(px)) return;
      const isBull = sig.direction === 'bull';
      const py     = isBull ? yScale(candle.low) + 40 : yScale(candle.high) - 40;
      const cg     = g.append('g').attr('class', 'combo-badge');
      // Larger diamond for combination signals — these are the moat
      const size = 7;
      cg.append('rect')
        .attr('x', px - size).attr('y', isBull ? py - size / 2 : py - size * 1.5)
        .attr('width', size * 2).attr('height', size * 2)
        .attr('transform', `rotate(45, ${px}, ${isBull ? py + size / 2 : py - size / 2})`)
        .attr('fill', `${sig.color}33`).attr('stroke', sig.color).attr('stroke-width', 1.2);
      cg.append('text')
        .attr('x', px).attr('y', isBull ? py + 4 : py - 4)
        .attr('text-anchor', 'middle').attr('fill', sig.color)
        .attr('font-size', '7px').attr('font-family', 'inherit').attr('font-weight', 'bold')
        .text(sig.label);
      // Condition count bubble (e.g. "3" conditions met)
      if (sig.conditions?.length > 1) {
        const bx = px + 8, by = isBull ? py - 10 : py + 4;
        cg.append('circle').attr('cx', bx).attr('cy', by).attr('r', 5)
          .attr('fill', sig.color).attr('opacity', 0.85);
        cg.append('text').attr('x', bx).attr('y', by + 3.5)
          .attr('text-anchor', 'middle').attr('fill', '#fff')
          .attr('font-size', '6px').attr('font-weight', 'bold')
          .text(sig.conditions.length);
      }
      cg.append('title').text(`${sig.desc}\n${(sig.conditions || []).join('\n')}`);
    });

    // Last price dashed line + solid pill on y-axis (S27QW11)
    g.selectAll('.last-price-g').remove();
    const lastCandle = chartDataRef.current[chartDataRef.current.length - 1];
    if (lastCandle) {
      const lpy   = yScale(lastCandle.close);
      const isPos = lastCandle.close >= lastCandle.open;
      const lpCol = isPos ? '#10b981' : '#ef4444';
      const lpg   = g.append('g').attr('class', 'last-price-g');
      lpg.append('line').attr('x1', 0).attr('y1', lpy).attr('x2', innerW).attr('y2', lpy)
        .attr('stroke', lpCol).attr('stroke-width', 1).attr('stroke-dasharray', '4,3').attr('opacity', 0.7);
      const pillG = lpg.append('g').attr('transform', `translate(${innerW + 2},${lpy - 9})`);
      pillG.append('rect').attr('x', 0).attr('y', 0).attr('width', 66).attr('height', 18).attr('rx', 3).attr('fill', lpCol);
      pillG.append('text').attr('x', 33).attr('y', 13).attr('text-anchor', 'middle')
        .attr('fill', '#ffffff').attr('font-size', '10px').attr('font-weight', 'bold').attr('font-family', 'inherit')
        .text(fmtPrice(lastCandle.close));
    }

    updateLegendValues(candlesRef.current, null);
  }, [updateLegendValues]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: updateLegendValues is the only function called. All other
  // variables are read through refs at call time — zero stale closure risk.

  // ── doRenderPanels — sub-panel repaint ────────────────────────────────────
  const doRenderPanels = useCallback(() => {
    const candles = candlesRef.current;
    if (!candles.length || !xScaleRef.current) return;

    const panelInds = activeIndRef.current.filter(i => INDICATOR_REGISTRY[i.key]?.type === 'panel');
    panelInds.forEach(ind => {
      const containerEl = panelContainersRef.current[ind.instanceId];
      if (!containerEl) return;
      const entry  = INDICATOR_REGISTRY[ind.key];
      const color  = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
      const result = entry.calc(candles, ind.params, activeTabRef.current === 0, color);
      const innerW = innerWRef.current;
      const panelH = 120; // S27FIX3: 80→120 for readable divergence patterns

      d3.select(containerEl).selectAll('svg.panel-svg').remove();
      const panelW = containerEl.clientWidth || innerW + CHART_MARGIN.left + CHART_MARGIN.right;
      const svg  = d3.select(containerEl).append('svg').attr('class', 'panel-svg')
        .attr('width', panelW).attr('height', panelH + 20).style('background', 'transparent');
      const pg   = svg.append('g').attr('transform', `translate(${CHART_MARGIN.left},5)`);

      let allVals = [];
      if (result.lines) result.lines.forEach(l => { allVals = allVals.concat(l.data.map(d => d.value)); });
      if (result.macd)  allVals = allVals.concat(result.macd.macdLine.map(d => d.value), result.macd.signalLine.map(d => d.value), result.macd.histogram.map(d => d.value));
      if (result.adx)   allVals = allVals.concat(result.adx.adx.map(d => d.value), result.adx.plusDI.map(d => d.value), result.adx.minusDI.map(d => d.value));

      const yMin   = entry.range ? entry.range[0] : (d3.min(allVals) || 0) * 0.95;
      const yMax   = entry.range ? entry.range[1] : (d3.max(allVals) || 100) * 1.05;
      const pYScale = d3.scaleLinear().domain([yMin, yMax]).range([panelH, 0]);
      const xScale  = xScaleRef.current;

      svg.append('g').attr('transform', `translate(${CHART_MARGIN.left + innerW},5)`)
        .call(d3.axisRight(pYScale).ticks(3).tickFormat(d => d.toFixed(1)))
        .call(ax => {
          ax.select('.domain').remove();
          ax.selectAll('text').attr('fill', '#8b8ba7').attr('font-size', '9px');
          ax.selectAll('line').attr('stroke', 'rgba(255,255,255,0.04)');
        });

      if (entry.refLines) entry.refLines.forEach(rl => {
        const y = pYScale(rl.value);
        pg.append('line').attr('x1', 0).attr('y1', y).attr('x2', innerW).attr('y2', y)
          .attr('stroke', rl.color).attr('stroke-width', 1).attr('stroke-dasharray', '4,3');
        if (rl.label) pg.append('text').attr('x', innerW - 2).attr('y', y - 2)
          .attr('fill', rl.color).attr('font-size', '8px').attr('text-anchor', 'end').text(rl.label);
      });

      const drawLine = (data, c) => {
        if (!data?.length) return;
        const lg = d3.line().x(d => xScale(new Date(d.time * 1000))).y(d => pYScale(d.value)).defined(d => d.value != null);
        pg.append('path').datum(data).attr('fill', 'none').attr('stroke', c).attr('stroke-width', 1.5).attr('d', lg);
      };

      if (result.lines) result.lines.forEach(l => drawLine(l.data, l.color));
      if (result.macd) {
        const { histogram, macdLine, signalLine } = result.macd;
        const bw   = Math.max(1, innerW / (histogram.length || 1) * 0.65);
        const zero = pYScale(0);
        pg.selectAll('rect.mhist').data(histogram).join('rect').attr('class', 'mhist')
          .attr('x', d => xScale(new Date(d.time * 1000)) - bw / 2)
          .attr('y', d => d.value >= 0 ? pYScale(d.value) : zero)
          .attr('width', bw)
          .attr('height', d => Math.abs(pYScale(d.value) - zero))
          .attr('fill', d => d.value >= 0 ? 'rgba(16,185,129,0.65)' : 'rgba(239,68,68,0.65)');
        drawLine(macdLine, color);
        drawLine(signalLine, color + '88');
      }
      if (result.adx) {
        drawLine(result.adx.adx, '#00d4ff');
        drawLine(result.adx.plusDI, '#10b981');
        drawLine(result.adx.minusDI, '#ef4444');
      }

      const paramStr = Object.entries(ind.params).filter(([k]) => k !== 'color').map(([, v]) => v).join(',');
      svg.append('text').attr('x', CHART_MARGIN.left + 4).attr('y', 14)
        .attr('fill', color).attr('font-size', '10px').attr('font-weight', '700')
        .text(entry.label + (paramStr ? ` (${paramStr})` : ''));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: reads activeIndRef, candlesRef, xScaleRef, innerWRef,
  // panelContainersRef — all stable refs. No closure over state needed.

  // ── SVG init — runs once on mount ─────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Fix 1: SVG dimensions read via getBoundingClientRect() after RAF.
    // Container uses flex:1 — getBCR is accurate only after first layout paint.
    // wrappedInit below handles the RAF + retry + cleanup registration.

    const buildSVG = (innerW, innerH) => {
      d3.select(container).selectAll('svg.mc').remove();
      const totalW = innerW + CHART_MARGIN.left + CHART_MARGIN.right;
      const totalH = innerH + CHART_MARGIN.top  + CHART_MARGIN.bottom;

      const svg = d3.select(container).append('svg').attr('class', 'mc')
        .attr('width', totalW).attr('height', totalH)
        .style('display', 'block').style('overflow', 'visible');

      const g = svg.append('g').attr('class', 'chart-g')
        .attr('transform', `translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`);
      gRef.current = g.node();

      // Layer order: grid → overlay → drawings → analyst → alerts → measure → crosshair
      g.append('g').attr('class', 'grid-h');
      g.append('g').attr('class', 'overlay-g');
      g.append('g').attr('class', 'drawing-layer');
      g.append('g').attr('class', 'analyst-layer');  // S27K
      g.append('g').attr('class', 'alerts-layer');   // S27K
      g.append('g').attr('class', 'measure-layer');
      g.append('g').attr('class', 'crosshair-g');

      svg.append('g').attr('class', 'x-axis')
        .attr('transform', `translate(${CHART_MARGIN.left},${CHART_MARGIN.top + innerH})`);
      svg.append('g').attr('class', 'y-axis')
        .attr('transform', `translate(${CHART_MARGIN.left + innerW},${CHART_MARGIN.top})`);

      // S27K: y-axis alert zone — transparent clickable rect in right margin
      svg.append('rect').attr('class', 'alert-zone')
        .attr('x', CHART_MARGIN.left + innerW).attr('y', CHART_MARGIN.top)
        .attr('width', CHART_MARGIN.right).attr('height', innerH)
        .attr('fill', 'transparent').attr('cursor', 'crosshair')
        .on('click', (event) => {
          if (!isLoggedInRef.current || !yScaleRef.current) return;
          const [, my] = d3.pointer(event, svg.node());
          const price  = yScaleRef.current.invert(my - CHART_MARGIN.top);
          if (!price || !isFinite(price) || price <= 0) return;
          setAlertPopover({ price: Math.round(price * 100) / 100, direction: 'above', saving: false, error: null });
        });

      // Drawing click handler (all drawing types)
      svg.on('click.draw', (event) => {
        setCtxMenu(null);
        const mode = drawingModeRef.current;
        if (!mode || !xScaleRef.current || !yScaleRef.current) return;
        const [mx, my] = d3.pointer(event, g.node());
        const price    = yScaleRef.current.invert(my);
        const date     = xScaleRef.current.invert(mx);
        if (price == null || !date) return;
        const ts = date.getTime() / 1000;
        _handleDrawingClick(mode, price, ts, innerW, setDrawings, setDrawingMode, setDrawingHint, logRef, saveDrawingRef, drawingIdRef, drawingsRef, trendStartRef, fibStartRef, rectStartRef, measureStartRef, measureRef, isDrawingRef, g);
      });

      // Right-click context menu
      svg.on('contextmenu.ctx', (event) => {
        event.preventDefault();
        if (!xScaleRef.current || !yScaleRef.current) return;
        const [mx, my]  = d3.pointer(event, g.node());
        const price     = yScaleRef.current.invert(my);
        const date      = xScaleRef.current.invert(mx);
        const bisect    = d3.bisector(d => d.date).left;
        const idx       = Math.min(bisect(chartDataRef.current, date), chartDataRef.current.length - 1);
        const candle    = chartDataRef.current[Math.max(0, idx)];
        const svgRect   = container.getBoundingClientRect();
        setCtxMenu({ x: event.clientX - svgRect.left, y: event.clientY - svgRect.top, price, date, candle });
      });

      // Mousemove — crosshair + tooltip
      svg.on('mousemove.ch', (event) => {
        if (crosshairLockedRef.current) return;
        if (!xScaleRef.current || !yScaleRef.current || !chartDataRef.current.length) return;
        const [mx, my] = d3.pointer(event, g.node());
        if (mx < 0 || mx > innerWRef.current || my < 0 || my > innerHRef.current) {
          setOhlcv(null); return;
        }
        const bisect = d3.bisector(d => d.date).left;
        const idx    = Math.min(bisect(chartDataRef.current, xScaleRef.current.invert(mx)), chartDataRef.current.length - 1);
        const candle = chartDataRef.current[Math.max(0, idx)];
        if (!candle) return;

        setOhlcv({ open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume, date: candle.date });
        ohlcvRef.current = candle;
        updateLegendValues(candlesRef.current, Math.max(0, idx));

        const containerRect = container.getBoundingClientRect();
        setTooltipPos({ x: event.clientX - containerRect.left, y: event.clientY - containerRect.top });

        // Draw crosshair lines + axis pills
        const cg  = d3.select(gRef.current).select('.crosshair-g');
        cg.selectAll('*').remove();
        const cx  = xScaleRef.current(candle.date);
        const cy  = yScaleRef.current(candle.close);
        const iW  = innerWRef.current;
        const iH  = innerHRef.current;
        cg.append('line').attr('x1', cx).attr('y1', 0).attr('x2', cx).attr('y2', iH)
          .attr('stroke', 'rgba(139,139,167,0.4)').attr('stroke-width', 1).attr('stroke-dasharray', '4,2');
        cg.append('line').attr('x1', 0).attr('y1', cy).attr('x2', iW).attr('y2', cy)
          .attr('stroke', 'rgba(139,139,167,0.4)').attr('stroke-width', 1).attr('stroke-dasharray', '4,2');

        // X-axis date pill
        const dateLabel = etFmt(candle.date, activeTabRef.current);
        const xLabelW   = activeTabRef.current <= 1 ? 80 : 58;
        const xLabelX   = Math.max(xLabelW / 2, Math.min(cx, iW - xLabelW / 2));
        const xLg       = cg.append('g').attr('transform', `translate(${xLabelX},${iH + 2})`);
        xLg.append('rect').attr('x', -xLabelW / 2).attr('y', 0).attr('width', xLabelW).attr('height', 18)
          .attr('rx', 3).attr('fill', '#1e2a3a').attr('stroke', 'rgba(0,212,255,0.35)').attr('stroke-width', 1);
        xLg.append('text').attr('x', 0).attr('y', 13).attr('text-anchor', 'middle')
          .attr('fill', '#00d4ff').attr('font-size', '10px').attr('font-family', 'inherit').text(dateLabel);

        // Y-axis price pill
        const yLg = cg.append('g').attr('transform', `translate(${iW + 2},${cy - 9})`);
        yLg.append('rect').attr('x', 0).attr('y', 0).attr('width', 64).attr('height', 18)
          .attr('rx', 3).attr('fill', '#1e2a3a').attr('stroke', 'rgba(0,212,255,0.35)').attr('stroke-width', 1);
        yLg.append('text').attr('x', 32).attr('y', 13).attr('text-anchor', 'middle')
          .attr('fill', '#00d4ff').attr('font-size', '10px').attr('font-family', 'inherit')
          .text('$' + candle.close.toFixed(2));

        // Crosshair hover D3 signal (debounced 2s)
        if (hoverDebounce.current) clearTimeout(hoverDebounce.current);
        hoverDebounce.current = setTimeout(() => logRef.current?.('crosshair_hover', { priceAtCrosshair: candle.close }), 2000);

        // Live measure ghost line during drag
        if (drawingModeRef.current === 'measure' && measureStartRef.current) {
          const mLayer    = d3.select(gRef.current).select('.measure-layer');
          const ghostState = { ...measureRef.current, ghostY: my, price2: yScaleRef.current.invert(my) };
          renderMeasure(mLayer, ghostState, xScaleRef.current, yScaleRef.current, iW);
        }
      });

      svg.on('mouseleave.ch', () => {
        d3.select(gRef.current)?.select('.crosshair-g').selectAll('*').remove();
        setOhlcv(null);
      });

      // D3 zoom
      const zoom = d3.zoom().scaleExtent([0.3, 80]).on('zoom', (event) => {
        if (!xScaleBaseRef.current) return;
        // S27FIX: rescale inner linear, re-wrap with trading-day index scale
        const rescaled = event.transform.rescaleX(xScaleBaseRef.current._linear);
        xScaleRef.current = createDateIndexScale(rescaled, tradingDatesRef.current, dateToIndexRef.current);
        doRenderChart();
        doRenderPanels();
      });
      zoomRef.current = zoom;
      svg.call(zoom);

      // ResizeObserver — responds to container size changes (Fix 1: updates innerH too)
      const ro = new ResizeObserver(() => {
        const r = container.getBoundingClientRect();
        if (!r.width) return;
        const niW = Math.max(100, r.width  - CHART_MARGIN.left - CHART_MARGIN.right);
        const niH = Math.max(60,  r.height - CHART_MARGIN.top  - CHART_MARGIN.bottom);
        innerWRef.current = niW;
        innerHRef.current = niH;
        svg.attr('width',  r.width).attr('height', r.height);
        // Update x-scale range
        if (xScaleBaseRef.current) xScaleBaseRef.current._linear.range([0, niW]);
        if (xScaleRef.current)     xScaleRef.current._linear.range([0, niW]);
        // Update y-axis and x-axis positions
        svg.select('.x-axis').attr('transform', `translate(${CHART_MARGIN.left},${CHART_MARGIN.top + niH})`);
        svg.select('.y-axis').attr('transform', `translate(${CHART_MARGIN.left + niW},${CHART_MARGIN.top})`);
        // Update alert zone
        svg.select('.alert-zone').attr('x', CHART_MARGIN.left + niW).attr('height', niH);
        doRenderChart();
        doRenderPanels();
      });
      ro.observe(container);

      // Keyboard navigation
      const handleKeyNav = (e) => {
        if (e.key === '?')      { setShowShortcuts(v => !v); return; }
        if (e.key === 'Escape') { setShowShortcuts(false); setCtxMenu(null); return; }
        if (e.key === 'l' || e.key === 'L') {
          const locked = !crosshairLockedRef.current;
          crosshairLockedRef.current = locked;
          setCrosshairLocked(locked);
          return;
        }
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const data = chartDataRef.current;
        if (!data.length || !xScaleRef.current || !yScaleRef.current) return;
        e.preventDefault();
        const curDate = ohlcvRef.current?.date;
        let idx = curDate ? data.findIndex(d => d.date.getTime() === curDate.getTime()) : data.length - 1;
        if (idx < 0) idx = data.length - 1;
        const nextIdx = e.key === 'ArrowLeft' ? Math.max(0, idx - 1) : Math.min(data.length - 1, idx + 1);
        const candle  = data[nextIdx];
        if (!candle) return;
        setOhlcv({ open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume, date: candle.date });
        ohlcvRef.current = candle;
        updateLegendValues(candlesRef.current, nextIdx);
        // Re-draw crosshair at new position
        const cx = xScaleRef.current(candle.date), cy = yScaleRef.current(candle.close);
        const iW = innerWRef.current,               iH = innerHRef.current;
        const cg = d3.select(gRef.current).select('.crosshair-g');
        cg.selectAll('*').remove();
        cg.append('line').attr('x1', cx).attr('y1', 0).attr('x2', cx).attr('y2', iH).attr('stroke', 'rgba(139,139,167,0.4)').attr('stroke-width', 1).attr('stroke-dasharray', '4,2');
        cg.append('line').attr('x1', 0).attr('y1', cy).attr('x2', iW).attr('y2', cy).attr('stroke', 'rgba(139,139,167,0.4)').attr('stroke-width', 1).attr('stroke-dasharray', '4,2');
        const xLW = activeTabRef.current <= 1 ? 80 : 58;
        const xLX = Math.max(xLW / 2, Math.min(cx, iW - xLW / 2));
        const xlg = cg.append('g').attr('transform', `translate(${xLX},${iH + 2})`);
        xlg.append('rect').attr('x', -xLW / 2).attr('y', 0).attr('width', xLW).attr('height', 18).attr('rx', 3).attr('fill', '#1e2a3a').attr('stroke', 'rgba(0,212,255,0.35)').attr('stroke-width', 1);
        xlg.append('text').attr('x', 0).attr('y', 13).attr('text-anchor', 'middle').attr('fill', '#00d4ff').attr('font-size', '10px').attr('font-family', 'inherit').text(etFmt(candle.date, activeTabRef.current));
        const ylg = cg.append('g').attr('transform', `translate(${iW + 2},${cy - 9})`);
        ylg.append('rect').attr('x', 0).attr('y', 0).attr('width', 64).attr('height', 18).attr('rx', 3).attr('fill', '#1e2a3a').attr('stroke', 'rgba(0,212,255,0.35)').attr('stroke-width', 1);
        ylg.append('text').attr('x', 32).attr('y', 13).attr('text-anchor', 'middle').attr('fill', '#00d4ff').attr('font-size', '10px').attr('font-family', 'inherit').text('$' + candle.close.toFixed(2));
      };
      container.addEventListener('keydown', handleKeyNav);

      return { ro, handleKeyNav };
    }; // end buildSVG — note: only called once from init()

    // Store cleanup handles so the effect cleanup can use them
    // Track handles for cleanup
    let ro            = null;  // ResizeObserver — must disconnect on unmount
    let keyHandler    = null;  // keydown listener — must remove on unmount
    let rafId         = null;  // requestAnimationFrame id — cancel if component
                               // unmounts before first paint (fast ticker changes)

    // Patch buildSVG to capture ro and keyHandler so cleanup can reach them
    const wrappedInit = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        rafId = requestAnimationFrame(wrappedInit);
        return;
      }
      // Safety net: if height is suspiciously small (< 200px), the parent container
      // doesn't have a fixed height. Calculate available space from container top to
      // viewport bottom instead. This handles cases where ChartPage uses height:auto.
      const effectiveH = rect.height < 200
        ? Math.max(400, window.innerHeight - rect.top - 40)
        : rect.height;
      const innerW = Math.max(100, rect.width   - CHART_MARGIN.left - CHART_MARGIN.right);
      const innerH = Math.max(60,  effectiveH   - CHART_MARGIN.top  - CHART_MARGIN.bottom);
      innerWRef.current = innerW;
      innerHRef.current = innerH;
      const handles = buildSVG(innerW, innerH);
      if (handles) { ro = handles.ro; keyHandler = handles.handleKeyNav; }
    };
    rafId = requestAnimationFrame(wrappedInit);

    return () => {
      // Cancel pending RAF — prevents stale SVG build after fast ticker change
      if (rafId) cancelAnimationFrame(rafId);
      // Disconnect ResizeObserver — prevents memory leak and callbacks on dead DOM
      if (ro) ro.disconnect();
      // Remove keydown listener — prevents ghost handlers accumulating
      if (keyHandler && container) container.removeEventListener('keydown', keyHandler);
      // Debounce timer
      if (hoverDebounce.current) clearTimeout(hoverDebounce.current);
      // Tear down SVG
      d3.select(container).selectAll('svg.mc').remove();
      gRef.current          = null;
      xScaleRef.current     = null;
      xScaleBaseRef.current = null;
      yScaleRef.current     = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: SVG init runs exactly once on mount. All dynamic state is
  // read through refs inside the callbacks — no re-init on prop changes needed.

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // DOM ref — attach to chart container div in PriceChart.jsx
    containerRef,

    // Data refs — pass these to useChartData as input params
    candlesRef, chartDataRef, tradingDatesRef, dateToIndexRef,
    xScaleRef, xScaleBaseRef, yScaleRef, zoomRef, gRef,
    innerWRef, innerHRef,
    wsRef, wsLiveRef, wsReconnectRef, wsTickerRef,
    showExtHoursRef, chartTypeRef,

    // Bridge refs — pass to useChartData; it writes, we read in doRenderChart
    earnEventsRef, signalBadgesRef, candlePatternsRef,

    // Panel containers — Sidebar sets panelContainersRef.current[instanceId]
    panelContainersRef,

    // Render callbacks — pass to useChartData
    doRenderChart, doRenderPanels,

    // Drawing refs — PriceChart.jsx passes saveDrawingToBackend here
    drawingIdRef, drawingModeRef, drawingDwellRef,

    // Visual state for JSX
    ohlcv, ohlcvRef,
    legendValues,
    tooltipPos,
    crosshairLocked, setCrosshairLocked,
    showShortcuts, setShowShortcuts,
    ctxMenu, setCtxMenu,
  };
}

// ── Drawing click handler (extracted to keep SVG init readable) ───────────────

function _handleDrawingClick(
  mode, price, ts, innerW,
  setDrawings, setDrawingMode, setDrawingHint, logRef, saveDrawingRef,
  drawingIdRef, drawingsRef, trendStartRef, fibStartRef, rectStartRef,
  measureStartRef, measureRef, isDrawingRef, g
) {
  const save = async (data) => {
    const mid = await saveDrawingRef?.current?.(data);
    if (mid) setDrawings(prev => prev.map(d => d.id === data._lid ? { ...d, mongoId: mid } : d));
  };

  if (mode === 'horizontal') {
    const lid = ++drawingIdRef.current;
    const color = DRAWING_COLORS.horizontal;
    logRef.current?.('drawing_created', { drawingType: 'horizontal', price });
    setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'horizontal', price, color, mongoId: null }]);
    setDrawingHint(null);
    save({ drawingType: 'horizontal', price: Math.round(price * 100) / 100, color });

  } else if (mode === 'trendline') {
    if (!isDrawingRef.current) {
      isDrawingRef.current = true;
      trendStartRef.current = { date: ts, price };
      setDrawingHint('Click a second point to complete the trendline');
    } else {
      const start = trendStartRef.current, lid = ++drawingIdRef.current, color = DRAWING_COLORS.trendline;
      isDrawingRef.current = false; trendStartRef.current = null;
      setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'trendline', date1: start.date, price1: start.price, date2: ts, price2: price, color, mongoId: null }]);
      setDrawingHint(null);
      logRef.current?.('drawing_created', { drawingType: 'trendline', price1: Math.round(start.price * 100) / 100, price2: Math.round(price * 100) / 100 });
      save({ drawingType: 'trendline', date1: start.date, price1: Math.round(start.price * 100) / 100, date2: ts, price2: Math.round(price * 100) / 100, color });
    }

  } else if (mode === 'fibonacci') {
    if (!fibStartRef.current) {
      fibStartRef.current = { date: ts, price };
      setDrawingHint('Click the swing low to complete Fibonacci levels');
    } else {
      const start = fibStartRef.current, lid = ++drawingIdRef.current, color = DRAWING_COLORS.fibonacci;
      fibStartRef.current = null; isDrawingRef.current = false;
      const priceHigh = Math.max(start.price, price), priceLow = Math.min(start.price, price);
      const date1 = start.price >= price ? start.date : ts, date2 = start.price >= price ? ts : start.date;
      setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'fibonacci', priceHigh, priceLow, date1, date2, color, mongoId: null }]);
      setDrawingHint(null);
      logRef.current?.('drawing_created', { drawingType: 'fibonacci', priceHigh: Math.round(priceHigh * 100) / 100, priceLow: Math.round(priceLow * 100) / 100, fibLevel618: Math.round((priceLow + (priceHigh - priceLow) * 0.618) * 100) / 100 });
      save({ drawingType: 'fibonacci', price1: Math.round(priceHigh * 100) / 100, price2: Math.round(priceLow * 100) / 100, logical1: date1, logical2: date2, color });
    }

  } else if (mode === 'rectangle') {
    if (!rectStartRef.current) {
      rectStartRef.current = { date: ts, price };
      setDrawingHint('Click the opposite corner to complete the zone');
    } else {
      const start = rectStartRef.current, lid = ++drawingIdRef.current, color = DRAWING_COLORS.rectangle;
      rectStartRef.current = null; isDrawingRef.current = false;
      const priceTop = Math.max(start.price, price), priceBottom = Math.min(start.price, price);
      const date1 = Math.min(start.date, ts), date2 = Math.max(start.date, ts);
      setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'rectangle', priceTop, priceBottom, date1, date2, color, mongoId: null }]);
      setDrawingHint(null);
      logRef.current?.('drawing_created', { drawingType: 'rectangle', priceTop: Math.round(priceTop * 100) / 100, priceBottom: Math.round(priceBottom * 100) / 100 });
      save({ drawingType: 'rectangle', price1: Math.round(priceTop * 100) / 100, price2: Math.round(priceBottom * 100) / 100, logical1: date1, logical2: date2, color });
    }

  } else if (mode === 'anchored_vwap') {
    const lid = ++drawingIdRef.current, color = DRAWING_COLORS.anchored_vwap;
    setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'anchored_vwap', anchorDate: ts, anchorPrice: price, color, mongoId: null }]);
    setDrawingHint(null);
    logRef.current?.('drawing_created', { drawingType: 'anchored_vwap', anchorDate: ts, anchorPrice: Math.round(price * 100) / 100 });
    save({ drawingType: 'anchored_vwap', anchorDate: ts, price: Math.round(price * 100) / 100, color });

  } else if (mode === 'ray') {
    const lid = ++drawingIdRef.current, color = DRAWING_COLORS.ray;
    setDrawings(prev => [...prev, { id: lid, _lid: lid, type: 'ray', price, date1: ts, color, mongoId: null }]);
    setDrawingHint(null);
    logRef.current?.('drawing_created', { drawingType: 'ray', price: Math.round(price * 100) / 100, originDate: ts });
    save({ drawingType: 'ray', price: Math.round(price * 100) / 100, logical1: ts, color });

  } else if (mode === 'measure') {
    if (!measureStartRef.current) {
      measureStartRef.current = { price, ts };
      measureRef.current = { price1: price, price2: null, dot1Y: null };
      setDrawingHint('Click a second point to complete the measurement');
    } else {
      const start = measureStartRef.current;
      measureStartRef.current = null;
      measureRef.current = { price1: start.price, price2: price, barCount: null, dot1Y: null, ghostY: null };
      setDrawingHint('Click anywhere to start a new measurement');
      const priceDelta = price - start.price;
      const pctDelta   = (priceDelta / start.price) * 100;
      logRef.current?.('price_measure', {
        priceDelta: Math.round(priceDelta * 100) / 100,
        pctDelta:   Math.round(pctDelta * 100) / 100,
        direction:  priceDelta >= 0 ? 'up' : 'down',
      });
    }
  }
}
